import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Scale, FileDown } from "lucide-react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";

const CATEGORY = "Solutions hospitalières proposées";
const LEVEL_LABEL = { "1": "Cabinet / centre", "1/2": "Cabinet → clinique", "2": "Clinique / hôpital", "2/3": "Hôpital → CHU", "3": "CHU / national" };
// Matrice : libellés fusionnés (« Compte rendu de séjour » → « Compte rendu ») et libellés génériques exclus (détaillés via solution_modules_matrice)
const FUSIONS = { "Compte rendu de séjour": "Compte rendu", "Stock": "Stock & Approvisionnement" };
const GENERIQUES = new Set(["Tous modules cliniques", "Tous modules"]);
const matrice = (p) => Array.from(new Set((p.solution_modules_matrice || p.solution_modules || []).map((m) => FUSIONS[m] || m)));


export default function ComparatifSolutions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState("Tous");

  useEffect(() => {
    api.get("/products", { params: { category: CATEGORY } })
      .then(({ data }) => setItems(data.filter((p) => p.sales_page).sort((a, b) => (a.solution_order || 99) - (b.solution_order || 99))))
      .catch((e) => toast.error(apiError(e, "Chargement impossible")))
      .finally(() => setLoading(false));
  }, []);

  const LEVEL_ORDER = ["1", "1/2", "2", "2/3", "3"];
  const levels = useMemo(() => ["Tous", ...Array.from(new Set(items.map((p) => p.solution_level))).sort((a, b) => LEVEL_ORDER.indexOf(a) - LEVEL_ORDER.indexOf(b))], [items]);
  const shown = level === "Tous" ? items : items.filter((p) => p.solution_level === level);
  const allModules = useMemo(() => Array.from(new Set(shown.flatMap((p) => matrice(p)).filter((m) => !GENERIQUES.has(m)))), [shown]);

  return (
    <div className="mx-auto max-w-[1600px] px-4 py-14 sm:px-6" data-testid="comparatif-page">
      <p className="font-mono text-xs uppercase tracking-widest text-ciel">Solutions Vitalis</p>
      <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Comparer les solutions</h1>
      <p className="mt-3 max-w-2xl text-sm text-slate-600">Niveau, licences, modules et tarification de chaque offre clés en main. Abonnement mensuel HT (TVA 18 % en sus) — licences, tablette, support et hébergement cloud inclus.</p>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2" data-testid="comparatif-level-filter">
          {levels.map((l) => (
            <button key={l} onClick={() => setLevel(l)} data-testid={`comparatif-level-${l.replace("/", "-")}`}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${level === l ? "bg-[#0369A1] text-white" : "border border-[#7DC4E8] bg-white text-slate-600 hover:text-[#0369A1]"}`}>
              {l === "Tous" ? "Tous les niveaux" : `Niveau ${l}`}
            </button>
          ))}
        </div>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/solutions/comparatif.pdf`} download data-testid="comparatif-pdf-btn"
          className="inline-flex items-center gap-1.5 rounded-full border border-[#7DC4E8] bg-white px-4 py-2 text-xs font-semibold text-[#0369A1] transition-colors hover:bg-[#E0F2FE]">
          <FileDown size={14} /> Télécharger en PDF
        </a>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-ciel" /></div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-[#7DC4E8] bg-white shadow-sm">
          <table className="w-full min-w-[960px] text-left text-sm" data-testid="comparatif-table">
            <thead className="bg-[#E0F2FE] text-[11px] font-semibold uppercase tracking-wider text-[#0369A1]">
              <tr>
                <th className="sticky left-0 bg-[#E0F2FE] px-4 py-3">Solution</th>
                <th className="px-4 py-3">Niveau</th>
                <th className="px-4 py-3">Modules</th>
                <th className="px-4 py-3 text-right">Licences</th>
                <th className="px-4 py-3 text-right">HT / mois</th>
                <th className="px-4 py-3 text-right">TTC / mois</th>
                <th className="px-4 py-3 text-right">Installation</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((p) => {
                return (
                  <tr key={p.product_id} className="align-top hover:bg-[#F8FBFE]" data-testid={`comparatif-row-${p.slug}`}>
                    <td className="sticky left-0 bg-white px-4 py-3">
                      <Link to={p.sales_page || `/produit/${p.product_id}`} className="font-semibold text-navy hover:text-[#0369A1]">{p.title}</Link>
                      <p className="mt-1 max-w-[220px] text-xs text-slate-500">{(p.short_description || "").split("\n")[0]}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex rounded-full bg-[#0369A1] px-2.5 py-0.5 text-[11px] font-semibold text-white">Niveau {p.solution_level}</span>
                      <p className="mt-1 text-[11px] text-slate-500">{LEVEL_LABEL[p.solution_level] || ""}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex max-w-[320px] flex-wrap gap-1">
                        {(p.solution_modules || []).map((m) => <span key={m} className="rounded-md border border-[#7DC4E8] bg-[#E0F2FE] px-1.5 py-0.5 text-[11px] text-[#0C4A6E]">{m}</span>)}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-slate-700">{p.solution_licences ? `${p.solution_licences} utilisateurs` : "—"}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-[#0369A1]">{p.solution_total_label}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-slate-700">{p.solution_ttc_label || "—"}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-slate-700">{p.solution_installation_label}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <Link to={p.sales_page || `/produit/${p.product_id}`} data-testid={`comparatif-cta-${p.slug}`} className="inline-flex rounded-full bg-[#0369A1] px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-[#075985]">
                        {p.cta_label || "Voir la fiche"}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && allModules.length > 0 && (
        <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6" data-testid="comparatif-matrix">
          <div className="flex items-center gap-2"><Scale size={16} className="text-[#0369A1]" /><h2 className="font-display text-base font-bold text-navy md:text-lg">Matrice des modules</h2></div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[960px] text-xs">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="sticky left-0 bg-white py-2 pr-4">Module</th>
                  {shown.map((p) => <th key={p.product_id} className="px-2 py-2 text-center font-medium">{p.title.replace("Vitalis ", "")}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {allModules.map((m) => (
                  <tr key={m}>
                    <td className="sticky left-0 bg-white py-2 pr-4 text-slate-700">{m}</td>
                    {shown.map((p) => <td key={p.product_id} className="px-2 py-2 text-center" data-testid={`matrice-${p.slug}-${m.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{matrice(p).includes(m) ? <Check size={14} className="mx-auto text-[#0369A1]" /> : <span className="text-slate-300">·</span>}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
